import React, { type ReactNode } from "react"
import { Image, Linking, TouchableOpacity, View } from "react-native"
import { formatDate } from "@/utils/DateUtils"
import { abbreviateCurrency, numberWithCommas } from "@/utils/NumberUtils"
import BedIcon from "@/assets/icons/bed-icon.svg"
import BathIcon from "@/assets/icons/bath-icon.svg"
import AreaIcon from "@/assets/icons/area-icon.svg"
import YearBuiltIcon from "@/assets/icons/year-built-icon.svg"
import FavoriteIcon from "@/assets/icons/heart.svg"
import IsFavoriteIcon from "@/assets/icons/heart-filled.svg"
import ShareIcon from "@/assets/icons/share-icon.svg"
import TrashIcon from "@/assets/icons/trash-icon.svg"
import { type AnalysisType } from "@/generated/graphql"
import { Tags } from "@/constants/Tags"
import { BFColors } from "@/constants/Colors"
import BFText from "@/components/standard/BFText"
import RingingNotifyIcon from "@/components/buttons/RingingNotifyIcon"

interface PropertyFeatureProps {
  icon: ReactNode
  value: string | number | null
}

const PropertyFeature: React.FC<PropertyFeatureProps> = ({
  icon,
  value,
}: PropertyFeatureProps) => (
  <View className="flex flex-row justify-center items-center px-3">
    {icon}
    <BFText className="font-spacegrotesk text-white text-[13px] pl-1">
      {value}
    </BFText>
  </View>
)

interface MoneyFeatureProps {
  title: string
  value: string | number | null
}

const MoneyFeature: React.FC<MoneyFeatureProps> = ({
  title,
  value,
}: MoneyFeatureProps) => (
  <View className="flex flex-col flex-1 items-center">
    <BFText className="font-spacegrotesk text-dark-grey text-[11px]">
      {title}
    </BFText>
    <BFText
      className={`font-figtree-bold text-[18px] ${(value + "").startsWith("-") ? "text-red" : "text-midnight-blue"}`}
    >
      {value}
    </BFText>
  </View>
)

interface TagProps {
  tag: string | null
}

export const Tag = ({ tag }: TagProps) => {
  if (!tag) {
    return <></>
  }

  const title: string = Tags[tag]
  let backgroundColor: string = BFColors.lightGrey
  let color: string = BFColors.midnightBlue
  switch (tag) {
    case "new":
      backgroundColor = BFColors.neonYellow
      break
    case "poor-deal":
      backgroundColor = BFColors.redOrange
      color = "#FFFFFF"
      break
    case "great-deal":
      backgroundColor = BFColors.teal
      break
    default:
      backgroundColor = BFColors.lightGrey
  }

  return (
    <View className="self-start rounded-[2px] mr-3" style={{ backgroundColor }}>
      <BFText
        className=" text-midnight-blue font-spacegrotesk text-[10px] px-4"
        style={{ color }}
      >
        {title}
      </BFText>
    </View>
  )
}

export interface PropertyCardProps {
  analysis: AnalysisType
  onShare: (id: string, street: string, imageUrl: string | null) => void
  onRemove: (id: string) => void
  onNotify: () => void
  onFavorite: () => void
  selected?: boolean
}

export default function PropertyCard({
  analysis,
  onShare,
  onRemove,
  selected,
  onNotify,
  onFavorite,
}: PropertyCardProps) {
  const displayNotificationIcon: boolean =
    analysis.isNotificationsEligible || false

  return (
    <View
      className={`bg-white flex flex-col flex-1 h-[290px] mb-6 rounded-[6px] overflow-hidden`}
    >
      <View
        className={`flex flex-1 h-[140px] rounded-[6px] overflow-hidden ${selected ? "border-gray-50 border bg-gray-100" : "border-warm-grey border"}`}
      >
        <Image
          className="overflow-hidden"
          source={{ uri: analysis.property.heroImage || "" }}
          resizeMode="cover"
          height={140}
          style={{ height: 140 }}
        />
        <View className="h-[140px]">
          <View className="flex flex-row mt-4">
            <MoneyFeature
              title="PURCHASE PRICE"
              value={abbreviateCurrency(analysis.purchasePrice)}
            />
            <MoneyFeature
              title="EST ARV"
              value={abbreviateCurrency(analysis.afterRepairValue) || "N/A"}
            />
            <MoneyFeature
              title="PROFIT POTENTIAL"
              value={abbreviateCurrency(analysis.profitPotential) || "N/A"}
            />
          </View>
          <View className="flex flex-row flex-1 px-4 pt-4">
            {analysis.tags?.map((tag) => <Tag key={`tag-${tag}`} tag={tag} />)}
          </View>
          <View className="flex flex-col">
            {analysis.isSourced ? (
              <>
                <BFText className="font-spacegrotesk text-dark-grey text-[10px] px-4">
                  CONTACT
                </BFText>
                <TouchableOpacity
                  testID={"source-email-link"}
                  onPress={() => {
                    void Linking.openURL(
                      `mailto:${analysis.sourcedProperty?.sourceEmail}`
                    )
                  }}
                >
                  <BFText className="text-calm-blue text-[12px] underline px-4 pb-4">
                    {analysis.sourcedProperty?.sourceEmail}
                  </BFText>
                </TouchableOpacity>
              </>
            ) : (
              <BFText className="text-[12px] font-spacegrotesk text-midnight-blue px-4 pb-4">
                {analysis.sharedFrom ? "Shared" : "Analyzed"} Property
              </BFText>
            )}
          </View>
        </View>
      </View>
      <View className="absolute flex w-full top-0 h-[140px] bg-black/50"></View>
      <View className="absolute flex flex-col justify-end top-0 inset-x-0 h-[140px] px-4">
        <View className="flex flex-row h-[40px] justify-between items-center mt-4">
          <BFText className="font-figtree-bold text-white text-[14px] ml-2">
            {formatDate(analysis.updatedAt, "M/d/yy")}
          </BFText>

          <View className="absolute top-0 right-0 flex flex-row items-center justify-center bg-off-white/35 px-5 rounded-full gap-5 py-2">
            {displayNotificationIcon && (
              <RingingNotifyIcon
                notificationsEnabled={analysis.notificationsEnabled || false}
                onNotify={onNotify}
              />
            )}

            <TouchableOpacity
              hitSlop={8}
              testID={"favorite-button"}
              onPress={onFavorite}
            >
              {analysis.isFavorite ? (
                <IsFavoriteIcon width={22} height={24} />
              ) : (
                <FavoriteIcon width={22} height={24} />
              )}
            </TouchableOpacity>
            <TouchableOpacity
              hitSlop={8}
              testID={"share-button"}
              onPress={() => {
                onShare(
                  analysis.id,
                  analysis.property.street,
                  analysis.property.heroImage || null
                )
              }}
            >
              <ShareIcon width={22} height={24} />
            </TouchableOpacity>
            <TouchableOpacity
              hitSlop={8}
              testID={"delete-button"}
              onPress={() => {
                onRemove(analysis.id)
              }}
            >
              <TrashIcon width={23} height={24} />
            </TouchableOpacity>
          </View>
        </View>
        <View className="flex flex-1" />
        <BFText className="font-figtree-bold text-white text-[14px] text-center">
          {analysis.property.street}
        </BFText>
        <BFText className="font-figtree-bold text-white text-[14px] text-center">
          {analysis.property.city}, {analysis.property.state}{" "}
          {analysis.property.zipcode}
        </BFText>
        <View className="flex flex-row justify-center py-2">
          <PropertyFeature
            icon={<BedIcon width={14} height={12} />}
            value={analysis.bedrooms || "-"}
          />
          <PropertyFeature
            icon={<BathIcon width={13} height={12} />}
            value={analysis.bathrooms || "-"}
          />
          <PropertyFeature
            icon={<AreaIcon width={13} height={14} />}
            value={`${analysis.area ? numberWithCommas(analysis.area) : "-"}`}
          />
          <PropertyFeature
            icon={<YearBuiltIcon width={13} height={12} />}
            value={analysis.yearBuilt || "-"}
          />
        </View>
      </View>
    </View>
  )
}
